import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  IconButton,
  Collapse,
  Rating,
  TextField
} from '@mui/material';
import {
  LocationOn,
  Phone,
  DirectionsCar,
  Schedule,
  Refresh,
  ExpandMore,
  ExpandLess,
  Navigation
} from '@mui/icons-material';
import axios from 'axios';
import LeafletMap from '../components/common/LeafletMap';
import L from 'leaflet';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const DriverUpdatePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobData, setJobData] = useState(null);
  // Post-completion survey state
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [driverLocation, setDriverLocation] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    jobDetails: true,
    driverInfo: true,
    statusHistory: false
  });

  // Map state
  const [mapInstance, setMapInstance] = useState(null);
  const [markers, setMarkers] = useState({});

  // Validate token format
  const isValidToken = (token) => {
    return token && token.length >= 32 && /^[A-Za-z0-9_-]+$/.test(token);
  };

  // Fetch job data
  const fetchJobData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/driver-updates/${token}`);
      if (response.data.success) {
        setJobData(response.data.data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        setError(response.data.message || 'Failed to fetch job data');
      }
    } catch (err) {
      console.error('Error fetching job data:', err);
      if (err.response?.status === 404) {
        setError('Invalid or expired tracking link. Please check your link or contact customer service.');
      } else {
        setError('Unable to load job information. Please try again later.');
      }
    }
  }, [token]);

  // Fetch driver location
  const fetchDriverLocation = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/driver-updates/${token}/location`);
      if (response.data.success) {
        setDriverLocation(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching driver location:', err);
      // Don't set error for location fetch failures as it's not critical
    }
  }, [token]);

  // Fetch job status
  const fetchJobStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/driver-updates/${token}/status`);
      if (response.data.success) {
        setJobStatus(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching job status:', err);
      // Don't set error for status fetch failures as it's not critical
    }
  }, [token]);

  // Refresh all data
  const submitFeedback = async () => {
    setFeedbackBusy(true);
    setFeedbackError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/driver-updates/${token}/feedback`, {
        rating: feedbackRating,
        comment: feedbackComment.trim()
      });
      setFeedbackDone(true);
    } catch (err) {
      setFeedbackError(err.response?.data?.message || 'Could not submit feedback — please try again');
    } finally {
      setFeedbackBusy(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchJobData(),
        fetchDriverLocation(),
        fetchJobStatus()
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  // Initial data load
  useEffect(() => {
    if (!isValidToken(token)) {
      setError('Invalid tracking token format');
      setLoading(false);
      return;
    }

    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchJobData(),
          fetchDriverLocation(),
          fetchJobStatus()
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [token, fetchJobData, fetchDriverLocation, fetchJobStatus]);

  // Set up auto-refresh
  useEffect(() => {
    if (!jobData) return;

    // Only auto-refresh for active jobs
    const activeStatuses = ['Dispatched', 'En Route', 'On Site', 'In-Progress'];
    if (!activeStatuses.includes(jobData.job?.status)) return;

    const interval = setInterval(() => {
      fetchDriverLocation();
      fetchJobStatus();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [jobData, fetchDriverLocation, fetchJobStatus]);

  // Update map when location data changes
  useEffect(() => {
    if (!mapInstance || !jobData) return;

    // Clear existing markers
    Object.values(markers).forEach(marker => {
      mapInstance.removeLayer(marker);
    });

    const newMarkers = {};

    // Add service location marker
    if (jobData.job.serviceLocation?.location?.coordinates) {
      const [lng, lat] = jobData.job.serviceLocation.location.coordinates;
      if (lat && lng) {
        const serviceMarker = L.marker([lat, lng], {
          icon: L.divIcon({
            className: 'custom-marker service-location',
            html: '<div style="background-color: #2196F3; color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; font-weight: bold;">S</div>',
            iconSize: [30, 30],
            iconAnchor: [15, 15]
          })
        }).addTo(mapInstance);
        
        serviceMarker.bindPopup(`
          <div>
            <strong>Service Location</strong><br/>
            ${jobData.job.serviceLocation.street}<br/>
            ${jobData.job.serviceLocation.city}, ${jobData.job.serviceLocation.state} ${jobData.job.serviceLocation.zip}
          </div>
        `);
        
        newMarkers.service = serviceMarker;
      }
    }

    // Add driver location marker if available
    if (driverLocation?.currentLocation) {
      const { lat, lng } = driverLocation.currentLocation;
      const driverMarker = L.marker([lat, lng], {
        icon: L.divIcon({
          className: 'custom-marker driver-location',
          html: '<div style="background-color: #4CAF50; color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; font-weight: bold;">D</div>',
          iconSize: [30, 30],
          iconAnchor: [15, 15]
        })
      }).addTo(mapInstance);
      
      driverMarker.bindPopup(`
        <div>
          <strong>Driver Location</strong><br/>
          ${driverLocation.driverName || 'Driver'}<br/>
          Last updated: ${new Date(driverLocation.currentLocation.timestamp).toLocaleTimeString()}
        </div>
      `);
      
      newMarkers.driver = driverMarker;
    }

    setMarkers(newMarkers);

    // Fit map to show all markers
    const markerPositions = Object.values(newMarkers).map(marker => marker.getLatLng());
    if (markerPositions.length > 0) {
      if (markerPositions.length === 1) {
        mapInstance.setView(markerPositions[0], 15);
      } else {
        const group = new L.featureGroup(Object.values(newMarkers));
        mapInstance.fitBounds(group.getBounds().pad(0.1));
      }
    }
  }, [mapInstance, jobData, driverLocation, markers]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Format status for display
  const getStatusColor = (status) => {
    const statusColors = {
      'Pending': 'default',
      'Dispatched': 'info',
      'En Route': 'warning',
      'On Site': 'primary',
      'In-Progress': 'primary',
      'Completed': 'success',
      'Canceled': 'error',
      'Unsuccessful': 'error'
    };
    return statusColors[status] || 'default';
  };

  // Format time for display
  const formatTime = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleString();
  };

  // Calculate ETA display
  const getETADisplay = () => {
    if (!jobStatus?.eta) return 'Not available';
    
    if (jobStatus.estimatedCompletion) {
      const now = new Date();
      const eta = new Date(jobStatus.estimatedCompletion);
      const diffMinutes = Math.round((eta - now) / (1000 * 60));
      
      if (diffMinutes > 0) {
        return `Approximately ${diffMinutes} minutes`;
      } else {
        return 'Should arrive soon';
      }
    }
    
    return `${jobStatus.eta} minutes`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading your service request...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Go to Home Page
        </Button>
      </Container>
    );
  }

  if (!jobData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          No job data available
        </Alert>
      </Container>
    );
  }

  const { job, permissions } = jobData;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header — white-labeled when the job came from a partner */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            {jobData?.branding?.logoUrl && (
              <Box
                component="img"
                src={jobData.branding.logoUrl}
                alt={jobData.branding.displayName}
                sx={{ height: 48, maxWidth: 160, objectFit: 'contain' }}
              />
            )}
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ color: jobData?.branding?.primaryColor || 'primary.main' }}
              >
                {jobData?.branding?.displayName || 'Service Request Tracking'}
              </Typography>
              {jobData?.branding && (
                <Typography variant="body2" color="text.secondary">
                  Roadside Assistance Tracking
                  {jobData.branding.supportPhone ? ` · Support: ${jobData.branding.supportPhone}` : ''}
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <IconButton 
              onClick={refreshData} 
              disabled={refreshing}
              color="primary"
            >
              <Refresh />
            </IconButton>
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Last updated: {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        </Box>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="h6">
              Job #{job.po || job._id}
            </Typography>
          </Grid>
          <Grid item>
            <Chip 
              label={job.status} 
              color={getStatusColor(job.status)}
              variant="filled"
            />
          </Grid>
          <Grid item xs>
            <Typography variant="body1" color="text.secondary">
              {job.service}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column - Job Details */}
        <Grid item xs={12} md={6}>
          {/* Job Details Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" component="h2">
                  Job Details
                </Typography>
                <IconButton onClick={() => toggleSection('jobDetails')}>
                  {expandedSections.jobDetails ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              
              <Collapse in={expandedSections.jobDetails}>
                <Box mt={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <LocationOn color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2">Service Location</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        {job.serviceLocation.street}<br/>
                        {job.serviceLocation.city}, {job.serviceLocation.state} {job.serviceLocation.zip}
                      </Typography>
                    </Grid>
                    
                    {job.dropoffLocation && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Navigation color="primary" sx={{ mr: 1 }} />
                          <Typography variant="subtitle2">Drop-off Location</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                          {job.dropoffLocation.street}<br/>
                          {job.dropoffLocation.city}, {job.dropoffLocation.state} {job.dropoffLocation.zip}
                        </Typography>
                      </Grid>
                    )}
                    
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <DirectionsCar color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2">Vehicle</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        {job.vehicle.year} {job.vehicle.make} {job.vehicle.model}
                        {job.vehicle.color && ` - ${job.vehicle.color}`}
                        {job.vehicle.license && <><br/>License: {job.vehicle.license}</>}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Schedule color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2">Created</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        {formatTime(job.createdAt)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          {/* Driver Information Card */}
          {job.driver && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" component="h2">
                    Driver Information
                  </Typography>
                  <IconButton onClick={() => toggleSection('driverInfo')}>
                    {expandedSections.driverInfo ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                
                <Collapse in={expandedSections.driverInfo}>
                  <Box mt={2}>
                    <Typography variant="subtitle1" gutterBottom>
                      {job.driver.name}
                    </Typography>
                    {job.driver.phone && (
                      <Box display="flex" alignItems="center" mb={1}>
                        <Phone color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {job.driver.phone}
                        </Typography>
                      </Box>
                    )}
                    {permissions.canViewETA && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Estimated Time of Arrival
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getETADisplay()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* Post-completion satisfaction survey */}
          {jobData?.job?.status === 'Completed' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                {jobData.job.customerSatisfaction?.submittedAt || feedbackDone ? (
                  <Box textAlign="center" py={1}>
                    <Typography variant="h6">Thank you for your feedback!</Typography>
                    <Rating
                      value={feedbackDone ? feedbackRating : jobData.job.customerSatisfaction?.rating || 0}
                      readOnly
                      size="large"
                    />
                  </Box>
                ) : (
                  <Box textAlign="center">
                    <Typography variant="h6" gutterBottom>
                      How was your service?
                    </Typography>
                    <Rating
                      value={feedbackRating}
                      onChange={(event, value) => setFeedbackRating(value)}
                      size="large"
                    />
                    <TextField
                      placeholder="Anything we should know? (optional)"
                      value={feedbackComment}
                      onChange={(event) => setFeedbackComment(event.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      sx={{ mt: 2 }}
                    />
                    {feedbackError && <Alert severity="error" sx={{ mt: 1 }}>{feedbackError}</Alert>}
                    <Button
                      variant="contained"
                      sx={{ mt: 2, bgcolor: jobData?.branding?.primaryColor || undefined }}
                      disabled={!feedbackRating || feedbackBusy}
                      onClick={submitFeedback}
                    >
                      {feedbackBusy ? 'Sending…' : 'Submit Feedback'}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Status History Card */}
          {permissions.canViewStatusHistory && jobStatus?.statusHistory && (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" component="h2">
                    Status History
                  </Typography>
                  <IconButton onClick={() => toggleSection('statusHistory')}>
                    {expandedSections.statusHistory ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                
                <Collapse in={expandedSections.statusHistory}>
                  <Box mt={2}>
                    {jobStatus.statusTimeline.map((item, index) => (
                      <Box key={index} mb={2}>
                        <Typography variant="subtitle2">
                          {item.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(item.timestamp)}
                        </Typography>
                        {index < jobStatus.statusTimeline.length - 1 && (
                          <Divider sx={{ mt: 1 }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Map */}
        <Grid item xs={12} md={6}>
          {permissions.canViewDriverLocation && (
            <Card sx={{ height: '600px' }}>
              <CardContent sx={{ height: '100%', p: 2 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  Live Tracking
                </Typography>
                <Box sx={{ height: 'calc(100% - 40px)', position: 'relative' }}>
                  <LeafletMap
                    mapId="driver-tracking-map"
                    center={[39.8283, -98.5795]}
                    zoom={4}
                    style={{ height: '100%', width: '100%' }}
                    whenCreated={setMapInstance}
                  />
                  {driverLocation?.currentLocation && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 10, 
                        right: 10, 
                        bgcolor: 'background.paper', 
                        p: 1, 
                        borderRadius: 1,
                        boxShadow: 1,
                        zIndex: 1000
                      }}
                    >
                      <Typography variant="caption" display="block">
                        Driver last seen:
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(driverLocation.currentLocation.timestamp).toLocaleTimeString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      <Box mt={4} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          Need help? Contact customer service for assistance.
        </Typography>
      </Box>
    </Container>
  );
};

export default DriverUpdatePage;